import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';

import { PageParameter } from '@blaze-shared/api-contracts-domain';
import { UrlVariable } from '@blaze-shared/projects-domain';
import { DatasetColumn, UrlParamsSchema } from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import * as runtimeSelectors from './runtime.selectors';

interface UrlParam {
  name: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class UrlParametersFacade {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly currentPageVariables$$ = new BehaviorSubject<UrlVariable[]>([]);

  private readonly urlParameters$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map((event) => {
      const url = new URL(`${window.location.origin}${event.urlAfterRedirects}`);
      const urlParams: UrlParam[] = [];
      url.searchParams.forEach((value, name) => {
        urlParams.push({ name, value });
      });
      return urlParams;
    }),
    shareReplay(1),
  );

  private readonly currentPageParameters$ = this.store.select(runtimeSelectors.selectPageId).pipe(
    switchMap((pageId) => this.getPageDefinedParameters$(pageId)),
    shareReplay(1),
  );

  currentPageVariables$ = this.currentPageVariables$$.asObservable();
  constructor() {
    combineLatest([this.currentPageParameters$, this.urlParameters$])
      .pipe(
        map(([pageDefinedParameters, urlParameter]) => {
          return this.mergeParameters(pageDefinedParameters, urlParameter);
        }),
      )
      .subscribe((params) => {
        this.currentPageVariables$$.next(params);
      });
  }

  getPageVariables$(pageId: string | undefined | null): Observable<UrlVariable[]> {
    const pageParams$ = pageId
      ? this.getPageDefinedParameters$(pageId)
      : this.currentPageParameters$;
    return combineLatest([pageParams$, this.urlParameters$]).pipe(
      map(([pageDefinedParameters, urlParameter]) => {
        return this.mergeParameters(pageDefinedParameters, urlParameter);
      }),
    );
  }

  getUrlSchema$(pageId: string | undefined | null): Observable<UrlParamsSchema | null> {
    if (!pageId) {
      return of(null);
    }
    return this.getPageVariables$(pageId).pipe(
      map((variables) => {
        if (variables.length === 0) {
          return null;
        }
        const columns = variables.map(
          ({ name }) =>
            ({
              name,
              columnType: 'String',
              dataType: 'String',
            }) as DatasetColumn,
        );
        return {
          columns,
          name: 'Url Parameters Schema',
          id: `urlParameters_${pageId}`,
          pageId,
        };
      }),
      distinctUntilChanged(isDeepEqual),
    );
  }

  getPageVariables(pageId: string | undefined): UrlVariable[] {
    let result: UrlVariable[] = [];
    this.getPageVariables$(pageId)
      .pipe(take(1))
      .subscribe((variables) => {
        result = variables;
      });
    return result;
  }

  private mergeParameters(
    pageDefinedParameters: PageParameter[],
    urlParameter: UrlParam[],
  ): UrlVariable[] {
    return pageDefinedParameters.map((pageDefinedParam) => {
      const urlParam = urlParameter.find((url) => url.name === pageDefinedParam.name);
      return {
        name: pageDefinedParam.name,
        value: urlParam ? urlParam.value : (pageDefinedParam.defaultValue ?? ''),
        source: 'query',
      } as UrlVariable;
    });
  }

  private getPageDefinedParameters$(pageId: string): Observable<PageParameter[]> {
    const pages$ = this.store.select(runtimeSelectors.selectPages);
    return pages$.pipe(
      map((pages) => {
        const page = pages.find((page) => page.id === pageId);
        return page?.urlParameters ?? [];
      }),
    );
  }
}
